import type { TileRef } from "../document/types.ts";
import { packOf } from "../editor/packs.ts";
import { TILE_PROP_CELLS_MAX, TILE_PROP_SIDE_MAX, TILE_PROP_STACK_MAX, isTilePropsTileset, type TilePropCell } from "./types.ts";

/**
 * Making an entry from a selection (ADR-102 decisions 13 and 14). The selection reaches this module as a plain
 * list of places, so it reads neither a tile palette's Sheet view nor a map's cell set: the tile and its place on
 * the sheet or the layer, mirrors included. A map's selection is one list for one layer, or one list for each
 * visible tile layer, lowest first, which stack at each place in that order.
 */

/**
 * One tile at one place of a sheet or a layer, in whole tiles from its top-left corner.
 */
export interface CapturePlace {
  readonly x: number;
  readonly y: number;
  readonly tile: TileRef;
}

/**
 * A place on a tileset's sheet, the tileset being named beside the list.
 */
export interface SheetPlace {
  readonly x: number;
  readonly y: number;
  readonly tile: number;
}

/**
 * A selection trimmed to its bounds: the pack its tiles belong to, the place of its top-left corner in the source
 * (so a first placement can start over the source cells), the frame's columns and rows, and the cells counted
 * from the corner, lowest layer first.
 */
export interface TileCapture {
  readonly pack: string;
  readonly origin: { readonly x: number; readonly y: number };
  readonly cols: number;
  readonly rows: number;
  readonly cells: readonly TilePropCell[];
}

/**
 * Captures the painted cells of a map's tile layers.
 *
 * @param layers The cells of one layer, or of each visible tile layer lowest first. The places of one layer are
 *   distinct.
 * @returns The capture, its cells in the order the layers and the lists give them.
 * @throws Error when nothing is selected; when a tile has no pack or is of the tile props tileset; when a tile
 *   belongs to a second pack (naming the first cell of it); when the bounds pass 30 columns or rows; when more
 *   than 4 tiles share a place; or when the cells pass 2,048.
 */
export function captureFromMapCells(layers: readonly (readonly CapturePlace[])[]): TileCapture {
  const places = layers.flat();
  const first = places[0];
  if (first === undefined) {
    throw new Error("nothing is selected: select at least one painted tile");
  }
  const pack = packOf(first.tile.tileset);
  const stacks = new Map<string, number>();
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const place of places) {
    const where = `(${place.x}, ${place.y})`;
    const placePack = packOf(place.tile.tileset);
    if (placePack === null) {
      throw new Error(`the tile at ${where} is of ${JSON.stringify(place.tile.tileset)}, which names no pack`);
    }
    if (placePack !== pack) {
      throw new Error(`the tile at ${where} is of the pack ${JSON.stringify(placePack)}, but the selection began in ${JSON.stringify(pack)}; a prop is made from one pack's tiles`);
    }
    if (isTilePropsTileset(place.tile.tileset)) {
      throw new Error(`the tile at ${where} is of the tile props tileset, which an entry may not draw from`);
    }
    const key = `${place.x},${place.y}`;
    const height = (stacks.get(key) ?? 0) + 1;
    if (height > TILE_PROP_STACK_MAX) {
      throw new Error(`${height} tiles are selected at ${where}; at most ${TILE_PROP_STACK_MAX} share a place`);
    }
    stacks.set(key, height);
    left = Math.min(left, place.x);
    top = Math.min(top, place.y);
    right = Math.max(right, place.x);
    bottom = Math.max(bottom, place.y);
  }
  const cols = right - left + 1;
  const rows = bottom - top + 1;
  if (cols > TILE_PROP_SIDE_MAX) {
    throw new Error(`the selection spans ${cols} columns; at most ${TILE_PROP_SIDE_MAX}`);
  }
  if (rows > TILE_PROP_SIDE_MAX) {
    throw new Error(`the selection spans ${rows} rows; at most ${TILE_PROP_SIDE_MAX}`);
  }
  if (places.length > TILE_PROP_CELLS_MAX) {
    throw new Error(`the selection holds ${places.length} tiles; at most ${TILE_PROP_CELLS_MAX.toLocaleString("en-US")}`);
  }
  return {
    pack: pack as string,
    origin: { x: left, y: top },
    cols,
    rows,
    cells: places.map((place) => ({ x: place.x - left, y: place.y - top, tile: place.tile })),
  };
}

/**
 * Captures a selection on one tileset's sheet.
 *
 * @param tileset The tileset the sheet shows.
 * @param places The selected places and the tile at each.
 * @returns The capture, as `captureFromMapCells` gives one for a single layer.
 * @throws Error as `captureFromMapCells` does.
 */
export function captureFromSheet(tileset: string, places: readonly SheetPlace[]): TileCapture {
  return captureFromMapCells([places.map((place) => ({ x: place.x, y: place.y, tile: { tileset, tile: place.tile } }))]);
}
